<template>
    <div ref="suggestionsField" v-on-click-outside="close" class="relative contents">
        <Float
            placement="bottom"
            :offset="6"
            as="div"
            class="relative"
            composable
            enter="transition duration-200 ease-out"
            enter-from="opacity-0 -translate-y-1"
            enter-to="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leave-from="opacity-100 translate-y-0"
            leave-to="opacity-0 -translate-y-1"
            :show="opened"
            floating-as="template"
        >
            <FloatReference>
                <input
                    :id="id"
                    ref="input"
                    v-model="modelValue"
                    :class="['forms__input', $attrs.class, sizeClass, { 'forms__input--disabled': props.disabled }]"
                    :placeholder="placeholder"
                    :type="props.type"
                    :dusk="dusk"
                    :autocomplete="autocomplete"
                    :required="required"
                    :disabled="disabled"
                    :min="props.min"
                    :max="props.max"
                    :tabindex="props.tabindex"
                    :step="props.step"
                    v-bind="$attrs"
                    @input="executeSearch(modelValue)"
                    @focusin="openIfOptionsAreNotEmpty"
                />
            </FloatReference>

            <FloatContent>
                <ui-popover class="!w-full">
                    <!-- Options: Search -->
                    <ui-popover-section v-if="searchable">
                        <ui-popover-header>
                            <AdminText
                                ref="searchField"
                                v-model="search"
                                :size="size"
                                :placeholder="lang('common::common.search_placeholder')"
                            />
                        </ui-popover-header>
                    </ui-popover-section>

                    <!-- Options -->
                    <ui-popover-segment>
                        <ui-scrollable class="max-h-80">
                            <ui-popover-link
                                v-for="(option, optionIndex) in filteredOptions"
                                :key="option"
                                :dusk="`${props.dusk}-option-${option[props.property]}`"
                                :clickable="true"
                                :selected="optionIndex === pointerIndex"
                                @click="select(option)"
                            >
                                {{ option }}
                            </ui-popover-link>
                        </ui-scrollable>
                    </ui-popover-segment>
                </ui-popover>
            </FloatContent>
        </Float>

        <div v-if="props.valid" class="absolute text-green-800 -translate-y-1/2 top-1/2 right-4">
            <icon icon="hero/outline/check" class="w-4 h-4"></icon>
        </div>

        <div v-if="props.invalid" class="absolute text-red-800 -translate-y-1/2 top-1/2 right-4">
            <icon icon="hero/outline/exclamation-circle" class="w-4 h-4"></icon>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { Ref } from 'vue';
import type { FieldComponent } from '@src/Common/Components/types';
import { vOnClickOutside } from '@vueuse/components';
import { Float, FloatReference, FloatContent } from '@headlessui-float/vue';

const modelValue = defineModel({
    type: [String, Number, Object, Array],
});

const props = withDefaults(defineProps<FieldComponent>(), {
    modelValue: null,
    options: () => [],
    placeholder: undefined,
    property: 'id',
    size: 'base',
    dusk: null,
    label: 'name',
    opened: false,
    valid: false,
    invalid: false,
    variant: null,
    disabled: false,
    multiple: false,
    removable: true,
    tabindex: null,
    ajaxRoute: null,
    preload: false,
    create: false,
    loadCurrent: false,
    params: () => ({}),
});

const emit = defineEmits<{
    (event: 'change', value: any): void;
}>();

const sizeClass: Ref<string> = computed((): string => {
    const sizes = {
        xs: 'forms__input--xs',
        sm: 'forms__input--sm',
        base: 'forms__input--base',
        lg: 'forms__input--lg',
        xl: 'forms__input--xl',
    };

    return `${sizes[props.size] ?? sizes.base} ${props.valid || props.invalid ? 'pr-8' : ''}`;
});

const options = ref(props.options);

const ajaxRoute = computed(() => props.field?.route ?? props.ajaxRoute);

const params = ref(props.params);

const suggestionsField: Ref<HTMLElement | null> = ref(null);

const search = ref('');

/**
 * Search data
 */
const executeSearch = (value: string) => {
    // Static-options usage has no ajax source — searching would overwrite
    // the options with an undefined payload and empty the dropdown.
    if (!ajaxRoute.value) {
        return;
    }

    axios
        .get(route(ajaxRoute.value), {
            params: {
                ...params.value,
                search: value,
            },
        })
        .then(({ data }) => {
            if (Array.isArray(data.data)) {
                options.value = data.data;
            }
        });
};

/**
 * Preload data
 */
const all = () => {
    if (!ajaxRoute.value) {
        return;
    }

    axios.get(route(ajaxRoute.value)).then(({ data }) => {
        if (Array.isArray(data.data)) {
            options.value = data.data;
        }
    });
};

if (props.preload) {
    all();
}

/**
 * Load current value
 */
const loadCurrent = () => {
    if (props.loadCurrent && modelValue.value && ajaxRoute.value) {
        axios.get(route(ajaxRoute.value, { id: modelValue.value })).then(({ data }) => {
            if (Array.isArray(data.data)) {
                options.value = data.data;
            }
        });
    }
};

if (props.loadCurrent) {
    loadCurrent();
}

/**
 * Opening
 */
const opened: Ref<boolean> = ref(props.opened ?? false);

const open = (): void => {
    opened.value = true;

    setTimeout(() => {
        suggestionsField.value?.focus();
    }, 1);
};

const openIfOptionsAreNotEmpty = (): void => {
    if (options.value.length > 0) {
        open();
    }

    if (props.searchable) {
        executeSearch('');
    }
};

const close = (): void => {
    opened.value = false;
};

const select = (option: string | Record<string, unknown>): void => {
    // Emit the same shape as free text: the option's property value, never the whole object
    modelValue.value = typeof option === 'object' && option !== null ? option[props.property] : option;
    close();
};

const filteredOptions = computed(() => {
    if (!props.searchable) {
        return options.value;
    }

    if (search.value === '') {
        return options.value;
    }

    return options.value.filter((item) => {
        if (typeof item === 'object') {
            return item[props.label].toLowerCase().includes(search.value.toLowerCase());
        }

        return item.toLowerCase().includes(search.value.toLowerCase());
    });
});

watch(
    () => options.value,
    () => {
        if (options.value.length > 0) {
            open();
        } else {
            close();
        }
    },
);

/**
 * Enable arrow navigation
 */
const pointerIndex: Ref<number> = ref(-1);

watch(opened, (value) => {
    if (value) {
        activateKeyboardNavigation();
    } else {
        deactivateKeyboardNavigation();
    }
});

watch(
    () => props.params,
    (value) => {
        params.value = value;
    },
    { deep: true },
);

const activateKeyboardNavigation = (): void => {
    document.addEventListener('keydown', arrowNavigation);
};

const deactivateKeyboardNavigation = (): void => {
    document.removeEventListener('keydown', arrowNavigation);
};

const arrowNavigation = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        pointerIndex.value = pointerIndex.value + 1;
    }

    if (event.key === 'ArrowUp') {
        event.preventDefault();
        pointerIndex.value = pointerIndex.value - 1;
    }

    if (event.key === 'Enter') {
        event.preventDefault();
        select(options.value[pointerIndex.value]);
    }

    if (event.key === 'Escape') {
        event.preventDefault();
        close();
    }
};

/**
 * Emit change
 */
watch(
    () => modelValue.value,
    (val) => {
        emit('change', val);
    },
);
</script>
